import { createMemo, createSignal } from 'solid-js';
import { COVERED, candles, closeAsOf, distributions, latestClose, latestCloseDate, name, pricedSymbols } from '~data';

// What the record actually says, from the first bar on file to the last.
//
// Only `close` is used. Open, high and low are nullable in the feed and absent
// for much of the history, so a calculation that reached for them would work on
// some funds and quietly not on others — and none of what is asked here needs
// an intraday range anyway.
//
// The two questions this answers are different and both matter:
//
//   Erosion   did the distribution come out of the fund's capital? A covered
//             call ETF can pay 12% forever while the unit price bleeds, and the
//             yield alone will never tell you. Price change and payout are
//             reported side by side so the trade is visible.
//
//   Backtest  what a position opened at the first bar would be worth now, taking
//             the cash or reinvesting it. Same engine, different question.
//
// A fund with no price history is absent rather than zeroed. As prices are
// scraped for more of the universe these tables fill in on their own.

const COVERED_SYMBOLS = COVERED.map((e) => e.symbol);

/** Covered funds that actually have a price series — the tables' whole scope.
    Not a fixed list: scrape prices for more of the universe and these fill in. */
export const PRICED_SYMBOLS = createMemo<string[]>(() => pricedSymbols(COVERED_SYMBOLS));

/** Covered funds with no price history yet, named rather than silently dropped
    — a table missing a third of the pool must not read as the whole pool. */
export const unpriced = createMemo<string[]>(() => COVERED_SYMBOLS.filter((s) => latestClose(s) == null));

export const asOf = createMemo<string | null>(() => {
    const dates = PRICED_SYMBOLS()
        .map((s) => latestCloseDate(s))
        .filter((d): d is string => d != null)
        .sort();
    return dates.at(-1) ?? null;
});

export interface FundRecord {
    symbol: string;
    name: string | null;
    from: string;
    to: string;
    years: number;
    firstClose: number;
    lastClose: number;
    /** Distribution dollars per unit paid over the window. */
    distPerUnit: number;
    payments: number;
    /** Percentage change in the unit price alone. Negative is erosion. */
    capitalPct: number;
    /** Distributions as a percentage of the opening price. */
    incomePct: number;
    /** capital + income. What the position actually returned, cash taken. */
    totalPct: number;
    /**
     * Share of everything the fund paid out that only replaced the capital it
     * lost. 100% means every dollar of distribution was your own money coming
     * back; 0% means the price held and the payout was all gain.
     */
    erosionShare: number | null;
}

const DAYS_PER_YEAR = 365.25;

const yearsBetween = (from: string, to: string): number => (Date.parse(to) - Date.parse(from)) / (DAYS_PER_YEAR * 24 * 60 * 60 * 1000);

/**
 * Every covered fund with a price history, worst capital change first — the
 * funds giving your money back to you dressed as income sort to the top, which
 * is the whole point of the table.
 */
export const records = createMemo<FundRecord[]>(() => {
    const out: FundRecord[] = [];

    for (const symbol of PRICED_SYMBOLS()) {
        const series = candles(symbol);
        if (series.length < 2) continue;

        const first = series[0];
        const last = series[series.length - 1];
        if (first.close <= 0) continue;

        // Only payments inside the price window: a distribution paid before the
        // first bar has no opening price to be measured against, and counting it
        // would inflate the income against a cost that was never paid.
        const paid = distributions(symbol).filter((d) => d.exDate >= first.date && d.exDate <= last.date);
        const distPerUnit = paid.reduce((sum, d) => sum + d.amount, 0);

        const capitalPct = ((last.close - first.close) / first.close) * 100;
        const incomePct = (distPerUnit / first.close) * 100;
        const lost = Math.max(0, first.close - last.close);

        out.push({
            symbol,
            name: name(symbol),
            from: first.date,
            to: last.date,
            years: yearsBetween(first.date, last.date),
            firstClose: first.close,
            lastClose: last.close,
            distPerUnit,
            payments: paid.length,
            capitalPct,
            incomePct,
            totalPct: capitalPct + incomePct,
            erosionShare: distPerUnit <= 0 ? null : Math.min(100, (lost / distPerUnit) * 100),
        });
    }

    return out.sort((a, b) => a.capitalPct - b.capitalPct);
});

// --- backtest ---------------------------------------------------------------

const [amount, setAmount] = createSignal(10_000);
export { amount, setAmount };

const [reinvest, setReinvest] = createSignal(true);
export { reinvest, setReinvest };

export interface Backtest {
    symbol: string;
    from: string;
    to: string;
    years: number;
    /** Units bought at the first close, and what that grew to under DRIP. */
    startUnits: number;
    endUnits: number;
    /** Distributions taken as cash, zero when reinvesting. */
    cash: number;
    /** Units at the last close, plus any cash taken. */
    endValue: number;
    /** Every distribution dollar the position received, reinvested or not. */
    received: number;
    profit: number;
    returnPct: number;
    /** Simple average per year, not a compounded rate — see the note below. */
    perYearPct: number;
}

/**
 * A position opened at the first bar and held to the last.
 *
 * Reinvesting buys at the close on the pay date (falling back to the ex-date
 * when the fund publishes no pay date), which is when the cash is actually in
 * hand. Fractional units are allowed: this is measuring the strategy, not
 * simulating a broker that would round you down and leave the remainder idle.
 *
 * `perYearPct` is the total divided by the number of years, not a CAGR. It is
 * the honest form for a return that includes distributions the holder may have
 * spent — compounding a figure whose cash was taken out would claim growth that
 * never happened.
 */
export const backtests = createMemo<Backtest[]>(() => {
    const out: Backtest[] = [];
    const invested = amount();
    const drip = reinvest();

    for (const symbol of PRICED_SYMBOLS()) {
        const series = candles(symbol);
        if (series.length < 2) continue;

        const first = series[0];
        const last = series[series.length - 1];
        if (first.close <= 0 || invested <= 0) continue;

        const startUnits = invested / first.close;
        let units = startUnits;
        let cash = 0;
        let received = 0;

        for (const d of distributions(symbol)) {
            if (d.exDate < first.date || d.exDate > last.date) continue;
            const payout = units * d.amount;
            received += payout;

            if (!drip) {
                cash += payout;
                continue;
            }

            // No price to reinvest at means the cash stays cash rather than
            // buying units at an invented one.
            const at = closeAsOf(symbol, d.payDate ?? d.exDate);
            if (at == null || at <= 0) cash += payout;
            else units += payout / at;
        }

        const endValue = units * last.close + cash;
        const years = yearsBetween(first.date, last.date);

        out.push({
            symbol,
            from: first.date,
            to: last.date,
            years,
            startUnits,
            endUnits: units,
            cash,
            endValue,
            received,
            profit: endValue - invested,
            returnPct: ((endValue - invested) / invested) * 100,
            perYearPct: years <= 0 ? 0 : (((endValue - invested) / invested) * 100) / years,
        });
    }

    return out.sort((a, b) => b.returnPct - a.returnPct);
});
